import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';

/**
 * Grade Horária da Turma.
 *
 * Define qual disciplina ocupa cada aula (período) de cada dia da semana.
 * É o que permite a frequência deixar de valer para o dia inteiro: o diário
 * e a chamada passam a apontar para uma aula específica, e a coordenação
 * consegue saber quais aulas de cada dia ficaram sem preenchimento.
 */

const WEEKDAYS = [
  { value: 0, label: 'Segunda' },
  { value: 1, label: 'Terça' },
  { value: 2, label: 'Quarta' },
  { value: 3, label: 'Quinta' },
  { value: 4, label: 'Sexta' },
  { value: 5, label: 'Sábado' },
];

const DEFAULT_PERIODS = 5;
const MAX_PERIODS = 12;

/**
 * Quantas linhas o quadro mostra: as aulas que a grade realmente tem.
 *
 * Era `Math.max(DEFAULT_PERIODS, maiorPeriodo)` — o quadro voltava para cinco
 * linhas a cada recarga, mesmo numa turma de duas aulas por dia, e não havia
 * como deixá-lo em duas: o coordenador diminuía, salvava, e a tela subia de
 * volta para cinco. No login do professor era pior, porque ele não tem o
 * botão: via três linhas vazias que não são aula nenhuma.
 *
 * DEFAULT_PERIODS fica como ponto de partida da grade em branco, onde não há
 * o que seguir.
 */
function linhasDoQuadro(maiorPeriodo) {
  return maiorPeriodo > 0 ? maiorPeriodo : DEFAULT_PERIODS;
}

/** Paleta estável por disciplina — a mesma matéria mantém a cor na grade. */
const SUBJECT_COLORS = [
  'bg-[#0974BA]', 'bg-[#13983D]', 'bg-[#F9B047]', 'bg-[#E83A3C]',
  'bg-[#7C3AED]', 'bg-[#0891B2]', 'bg-[#DB2777]', 'bg-[#65A30D]',
];

function subjectColor(idSubject) {
  if (!idSubject) return 'bg-slate-400';
  return SUBJECT_COLORS[Number(idSubject) % SUBJECT_COLORS.length];
}

function slotKey(weekday, period) {
  return `${weekday}-${period}`;
}

function GradeHoraria() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  const [turma, setTurma] = useState(null);
  const [assignments, setAssignments] = useState([]);
  // Todas as disciplinas da rede e os professores lotados na escola. A grade
  // é a porta de entrada do cadastro: o que faltar de vínculo
  // disciplina-professor nasce dela ao salvar.
  const [opcoes, setOpcoes] = useState({ subjects: [], teachers: [] });
  const [confirmarVinculos, setConfirmarVinculos] = useState(null);
  // Professores que ficariam em duas turmas no mesmo horário. É aviso, não
  // bloqueio: uma grade real pode ter arranjos que o sistema não conhece.
  const [choques, setChoques] = useState(null);
  const [slots, setSlots] = useState({});
  const [periods, setPeriods] = useState(DEFAULT_PERIODS);
  const [schoolYear, setSchoolYear] = useState(new Date().getFullYear());
  // A partir de quando a grade passa a ser cobrada no acompanhamento. Em
  // branco, vale a partir do cadastro — assim montar a grade hoje não
  // transforma o ano inteiro em pendência.
  const [validFrom, setValidFrom] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(null); // { weekday, period }
  // Proposta gerada pelo backend, ainda não salva. Fica destacada até o
  // coordenador homologar: a grade só vale depois que ele confere.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const canEdit = [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA].includes(user?.role);
  // O professor não monta a grade, mas usa o quadro para chegar às SUAS aulas:
  // as dele abrem a chamada, as dos colegas ficam apagadas e sem clique.
  const ehProfessor = user?.role === ROLES.PROFESSOR;
  const meuCadastro = user?.id_teacher || null;
  // O slot guarda o id do professor como string (ver loadSlots) e o usuário
  // traz número: comparar sem normalizar não casa nunca.
  const minhaAula = (slot) =>
    ehProfessor && slot && meuCadastro
    && String(slot.id_teacher) === String(meuCadastro);

  // A célula é um dia da semana, não uma data. O clique precisa de uma:
  // a ocorrência desse dia na semana corrente, que é a aula que o professor
  // tem em mãos.
  const dataDoDiaNaSemana = (weekday) => {
    const hoje = new Date();
    const segunda = new Date(hoje);
    segunda.setDate(hoje.getDate() - ((hoje.getDay() + 6) % 7));
    segunda.setDate(segunda.getDate() + weekday);
    const mes = String(segunda.getMonth() + 1).padStart(2, '0');
    const dia = String(segunda.getDate()).padStart(2, '0');
    return `${segunda.getFullYear()}-${mes}-${dia}`;
  };

  // A chamada vive na aba "Frequência" do Diário. Data e período viajam na
  // URL para a aba já abrir na aula desta célula do quadro.
  const abrirMinhaAula = (weekday, period) => {
    navigate(
      `/turmas/${id}/diario?tab=frequencia`
      + `&data=${dataDoDiaNaSemana(weekday)}&aula=${period}`
    );
  };

  const card = darkMode
    ? 'bg-slate-800/50 border border-slate-700/10'
    : 'bg-white/80 border border-slate-200 shadow-sm';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';
  const inputCls = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
  }`;

  const loadSlots = useCallback(async (year) => {
    const grade = await apiGet('/class-schedule/by_class/', {
      id_class: id,
      school_year: year,
    });
    const next = {};
    let maiorPeriodo = 0;
    let vigencia = '';
    grade.weekdays.forEach(dia => {
      dia.slots.forEach(s => {
        if (s.valid_from && (!vigencia || s.valid_from < vigencia)) vigencia = s.valid_from;
        next[slotKey(s.weekday, s.period)] = {
          id_subject: String(s.id_subject),
          id_teacher: s.id_teacher ? String(s.id_teacher) : '',
          start_time: s.start_time ? s.start_time.slice(0, 5) : '',
          end_time: s.end_time ? s.end_time.slice(0, 5) : '',
        };
        if (s.period > maiorPeriodo) maiorPeriodo = s.period;
      });
    });
    setSlots(next);
    setPeriods(linhasDoQuadro(maiorPeriodo));
    setValidFrom(vigencia);
  }, [id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const turmaData = await apiGet(`/classes/${id}/`);
      setTurma(turmaData);
      const ano = turmaData.school_year || new Date().getFullYear();
      setSchoolYear(ano);

      const [assignmentsData, opcoesData] = await Promise.all([
        fetchAllPaginated('/class-subjects/', { id_class: id, is_active: true })
          .catch(() => []),
        apiGet('/class-schedule/options/', { id_class: id })
          .catch(() => ({ subjects: [], teachers: [] })),
      ]);
      setAssignments(assignmentsData);
      setOpcoes(opcoesData);

      await loadSlots(ano);
    } catch (err) {
      console.error('Erro ao carregar grade:', err);
      setError('Não foi possível carregar a grade horária desta turma.');
    } finally {
      setLoading(false);
    }
  }, [id, loadSlots]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Quantos dos nomes oferecidos estão de fato lotados nesta escola. O
  // backend manda o número; `in_school` por professor é o resguardo para uma
  // resposta antiga, servida de cache, que ainda não traga o campo.
  const teachersNaEscola = opcoes.teachers_in_school ?? (
    (opcoes.teachers || []).filter(t => t.in_school).length
  );

  // Uma disciplina pode ter MAIS DE UM professor na mesma turma — duas
  // professoras de História em turmas diferentes do mesmo 7º ano, por
  // exemplo. Por isso o nome exibido na célula vem do professor do SLOT,
  // não da atribuição da disciplina (que guarda só uma das pessoas).
  const teachersById = useMemo(() => {
    const map = {};
    (opcoes.teachers || []).forEach(t => { map[String(t.id)] = t.teacher_name; });
    assignments.forEach(a => {
      if (a.id_teacher && !map[String(a.id_teacher)]) map[String(a.id_teacher)] = a.teacher_name;
    });
    return map;
  }, [opcoes, assignments]);

  const subjectNameById = useMemo(() => {
    const map = {};
    (opcoes.subjects || []).forEach(s => { map[String(s.id)] = s.subject_name; });
    assignments.forEach(a => {
      if (!map[String(a.id_subject)]) map[String(a.id_subject)] = a.subject_name;
    });
    return map;
  }, [opcoes, assignments]);

  // Pares (disciplina, professor) que a turma já tem cadastrados.
  const vinculosExistentes = useMemo(
    () => new Set(assignments.map(a => `${a.id_subject}-${a.id_teacher}`)),
    [assignments]
  );

  // O que a grade vai criar quando for salva. Criar o vínculo é o que dá ao
  // professor permissão de escrever no diário — então a coordenação confirma.
  const vinculosNovos = useMemo(() => {
    const novos = new Map();
    Object.values(slots).forEach(s => {
      if (!s.id_subject || !s.id_teacher) return;
      const chave = `${s.id_subject}-${s.id_teacher}`;
      if (vinculosExistentes.has(chave)) return;
      const atual = novos.get(chave);
      novos.set(chave, {
        id_subject: s.id_subject,
        id_teacher: s.id_teacher,
        subject_name: subjectNameById[s.id_subject] || 'Disciplina',
        teacher_name: teachersById[s.id_teacher] || 'Professor',
        aulas: (atual?.aulas || 0) + 1,
      });
    });
    return [...novos.values()];
  }, [slots, vinculosExistentes, subjectNameById, teachersById]);


  const totalSlots = Object.keys(slots).length;

  // A última aula que tem disciplina. Tirar linha abaixo disso esconderia uma
  // aula que continua na grade — e o que está escondido é salvo do mesmo
  // jeito, então o coordenador não teria como perceber.
  const maiorPeriodoPreenchido = useMemo(
    () => Object.keys(slots).reduce(
      (maior, key) => Math.max(maior, Number(key.split('-')[1]) || 0), 0
    ),
    [slots]
  );
  const pisoDeLinhas = Math.max(1, maiorPeriodoPreenchido);

  const cargaPorDisciplina = useMemo(() => {
    const counts = {};
    Object.values(slots).forEach(s => {
      counts[s.id_subject] = (counts[s.id_subject] || 0) + 1;
    });
    return counts;
  }, [slots]);

  const handleSuggest = async () => {
    setSuggesting(true);
    setError(null);
    setSuccess(null);
    try {
      const proposta = await apiGet('/class-schedule/suggest/', { id_class: id });
      if (!proposta.slots.length) {
        setError(proposta.message || 'Não foi possível propor uma grade para esta turma.');
        setSuggestion(null);
        return;
      }
      const next = {};
      let maiorPeriodo = 0;
      proposta.slots.forEach(s => {
        next[slotKey(s.weekday, s.period)] = {
          id_subject: String(s.id_subject),
          id_teacher: s.id_teacher ? String(s.id_teacher) : '',
          start_time: s.start_time || '',
          end_time: s.end_time || '',
        };
        if (s.period > maiorPeriodo) maiorPeriodo = s.period;
      });
      setSlots(next);
      setPeriods(linhasDoQuadro(maiorPeriodo));
      setSuggestion(proposta);
    } catch (err) {
      console.error('Erro ao sugerir grade:', err);
      setError(err.message || 'Erro ao gerar a proposta de grade.');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSetSlot = (weekday, period, value) => {
    setSuccess(null);
    setSlots(prev => {
      const next = { ...prev };
      if (!value || !value.id_subject) {
        delete next[slotKey(weekday, period)];
      } else {
        next[slotKey(weekday, period)] = value;
      }
      return next;
    });
  };

  const handleCopyDay = (fromWeekday, toWeekday) => {
    setSuccess(null);
    setSlots(prev => {
      const next = { ...prev };
      for (let p = 1; p <= periods; p += 1) {
        delete next[slotKey(toWeekday, p)];
        const origem = prev[slotKey(fromWeekday, p)];
        if (origem) next[slotKey(toWeekday, p)] = { ...origem };
      }
      return next;
    });
  };

  const payloadSlots = () => Object.entries(slots).map(([key, value]) => {
    const [weekday, period] = key.split('-').map(Number);
    return {
      weekday,
      period,
      id_subject: parseInt(value.id_subject, 10),
      id_teacher: value.id_teacher ? parseInt(value.id_teacher, 10) : null,
      start_time: value.start_time || null,
      end_time: value.end_time || null,
      valid_from: validFrom || null,
    };
  });

  const handleSave = async ({ confirmado = false, ignorarChoques = false } = {}) => {
    if (!confirmado && !ignorarChoques) {
      // Confere o choque de professor antes de gravar: depois de salvo o
      // aviso chegaria tarde.
      try {
        const resp = await apiPost('/class-schedule/check-conflicts/', {
          id_class: parseInt(id, 10),
          school_year: schoolYear,
          valid_from: validFrom || undefined,
          slots: payloadSlots(),
        });
        if (resp.total > 0) {
          setChoques(resp.teacher_conflicts);
          return;
        }
      } catch (err) {
        // A conferência é auxiliar: se falhar, o salvamento segue.
        console.error('Erro ao conferir choques de horário:', err);
      }
    }
    setChoques(null);
    if (vinculosNovos.length && !confirmado) {
      setConfirmarVinculos(vinculosNovos);
      return;
    }
    setConfirmarVinculos(null);
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = payloadSlots();

      const resp = await apiPost('/class-schedule/bulk_save/', {
        id_class: parseInt(id, 10),
        school_year: schoolYear,
        // A partir de quando a nova versão vale. O que já passou continua
        // sendo julgado pela grade que valia na época.
        valid_from: validFrom || undefined,
        slots: payload,
      });

      if (resp.errors && resp.errors.length) {
        setError(`Alguns horários não foram salvos: ${resp.errors.join(' | ')}`);
      } else {
        const partes = [`${resp.created} nova(s)`];
        if (resp.updated) partes.push(`${resp.updated} corrigida(s)`);
        if (resp.unchanged) partes.push(`${resp.unchanged} sem mudança`);
        if (resp.closed) partes.push(`${resp.closed} encerrada(s)`);
        const criados = resp.assignments_created || [];
        setSuccess(
          `Grade homologada a partir de ${resp.valid_from.split('-').reverse().join('/')}: ` +
          `${partes.join(', ')}.` +
          (criados.length
            ? ` ${criados.length} vínculo(s) disciplina-professor criado(s): ` +
              criados.map(v => `${v.subject_name} · ${v.teacher_name}`).join('; ') + '.'
            : '')
        );
        setSuggestion(null);
      }
      await fetchData();
    } catch (err) {
      console.error('Erro ao salvar grade:', err);
      setError(err.message || 'Erro ao salvar a grade horária.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Grade Horária..."
        subtitle="Buscando disciplinas e horários da turma..."
        size="xl"
      />
    );
  }

  return (
    <>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/turmas')}
              className={`p-2 rounded-lg transition-colors ${darkMode
                ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200'
                : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'
              }`}
              title="Voltar para Turmas"
            >
              ←
            </button>
            <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
              ? 'bg-gradient-to-r from-gray-50 to-gray-400'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
              Grade Horária
            </h1>
          </div>
          <p className={`text-sm ${muted}`}>
            {turma?.class_name} — {turma?.school_name}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Ano letivo</label>
            <input
              type="number"
              value={schoolYear}
              onChange={async (e) => {
                const ano = parseInt(e.target.value, 10);
                setSchoolYear(ano);
                if (String(ano).length === 4) {
                  try { await loadSlots(ano); } catch { /* ano sem grade ainda */ }
                }
              }}
              className={`${inputCls} w-28`}
            />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${muted}`}>Vigente a partir de</label>
            <input
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              disabled={!canEdit}
              title="Em branco: a grade passa a valer a partir de hoje, sem cobrar dias anteriores."
              className={`${inputCls} w-40`}
            />
          </div>
          {canEdit && (
            <button
              onClick={handleSuggest}
              disabled={suggesting || saving}
              title="Monta uma proposta a partir dos diários já lançados e da carga horária cadastrada. Nada é salvo até você conferir."
              className={`px-4 py-2.5 mt-5 rounded-xl text-sm font-medium transition-all ${
                suggesting
                  ? 'bg-slate-500 text-white cursor-not-allowed'
                  : darkMode
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {suggesting ? 'Montando...' : 'Sugerir grade'}
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-5 py-2.5 mt-5 rounded-xl text-sm font-medium text-white transition-all ${
                saving
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25'
              }`}
            >
              {saving ? 'Salvando...' : 'Salvar Grade'}
            </button>
          )}
        </div>
      </header>

      <div className={`rounded-xl p-4 mb-6 ${darkMode ? 'bg-[#0974BA]/10 border border-[#0974BA]/20' : 'bg-[#0974BA]/5 border border-[#0974BA]/20'}`}>
        {ehProfessor ? (
          <>
            <p className={`text-sm ${strong}`}>
              Este é o quadro da turma inteira. <strong>As suas aulas estão
              destacadas</strong> — clique numa delas para abrir a chamada desta
              semana já no horário certo.
            </p>
            <p className={`text-xs mt-2 ${muted}`}>
              As aulas apagadas são de outros professores: aparecem para você saber
              o que vem antes e depois da sua, mas só quem leciona a aula lança a
              frequência dela. Quem monta este quadro é a coordenação.
            </p>
          </>
        ) : (
          <>
            <p className={`text-sm ${strong}`}>
              Cada célula é uma aula. É ela que o professor preenche no diário e onde a
              frequência é lançada — por isso a chamada vale só para aquele horário, e não
              para o dia inteiro.
            </p>
            <p className={`text-xs mt-2 ${muted}`}>
              O acompanhamento só cobra as aulas a partir da data de vigência — em branco,
              vale de hoje em diante. Mudar um horário <strong>não reescreve o passado</strong>:
              a versão anterior é encerrada na véspera e a nova passa a valer, para que
              a cobrança de cada mês continue sendo julgada pela grade daquele mês.
            </p>
          </>
        )}
      </div>

      {suggestion && (
        <div className={`p-4 rounded-xl mb-4 border-2 border-dashed ${darkMode
          ? 'bg-[#7C3AED]/10 border-[#7C3AED]/40'
          : 'bg-[#7C3AED]/5 border-[#7C3AED]/40'}`}>
          <p className="text-sm font-semibold text-[#7C3AED] mb-1">
            Proposta gerada — ainda não salva
          </p>
          <p className={`text-xs ${muted}`}>
            {suggestion.history_entries > 0
              ? `Baseada em ${suggestion.history_entries} aula(s) já lançada(s) nos últimos ${suggestion.lookback_days} dias e na carga horária cadastrada.`
              : 'Baseada na carga horária cadastrada — ainda não há diários suficientes para inferir os dias.'}
            {' '}Confira os horários e clique em <strong>Salvar Grade</strong> para homologar.
          </p>
          {suggestion.warnings?.length > 0 && (
            <ul className={`mt-2 text-xs space-y-1 ${muted}`}>
              {suggestion.warnings.map((w, i) => (
                <li key={i}>• {w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
          <p className="text-sm text-[#E83A3C]">{error}</p>
        </div>
      )}
      {success && (
        <div className={`p-4 rounded-xl mb-4 ${darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/20' : 'bg-[#13983D]/5 border border-[#13983D]/20'}`}>
          <p className="text-sm text-[#13983D]">{success}</p>
        </div>
      )}

      {/* A lista de professores da célula sai da LOTAÇÃO na escola desta
          turma, unida a quem já tem atribuição aqui. Quando a coordenação foi
          montar a grade da Paulo Maroja, apareceram só dois nomes — os que
          tinham atribuição — e nada na tela dizia que os recém-cadastrados
          estavam sem lotação. O aviso agora sai pelo número de lotados, e não
          por a lista estar totalmente vazia: dois nomes herdados escondiam o
          problema tão bem quanto zero. */}
      {teachersNaEscola === 0 && (
        <div className={`rounded-xl p-4 mb-6 ${darkMode ? 'bg-[#F9B047]/10 border border-[#F9B047]/20' : 'bg-[#F9B047]/5 border border-[#F9B047]/20'}`}>
          <p className="text-sm font-medium text-[#F9B047] mb-1">
            Nenhum professor lotado em {opcoes.school_name || 'nesta escola'}.
          </p>
          <p className={`text-xs ${muted}`}>
            {(opcoes.teachers || []).length > 0
              ? `A lista da célula traz só ${opcoes.teachers.length} nome(s), que vieram de atribuições já existentes nesta turma. `
              : ''}
            Para os demais aparecerem, cadastre a lotação deles em RH —
            Funcionários, marcando esta escola.
          </p>
        </div>
      )}

      {canEdit && vinculosNovos.length > 0 && !confirmarVinculos && (
        <div className={`rounded-xl p-4 mb-4 ${darkMode
          ? 'bg-[#0974BA]/10 border border-[#0974BA]/25'
          : 'bg-[#0974BA]/5 border border-[#0974BA]/25'}`}>
          <p className="text-sm font-semibold text-[#0974BA] mb-1">
            {vinculosNovos.length} vínculo(s) disciplina-professor serão criados ao salvar
          </p>
          <p className={`text-xs ${muted}`}>
            {vinculosNovos.map(v => `${v.subject_name} · ${v.teacher_name}`).join(' — ')}
          </p>
        </div>
      )}

      <div className={`rounded-xl p-5 ${card}`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className={`text-sm font-semibold uppercase tracking-wider ${muted}`}>
            Quadro semanal — {totalSlots} aula(s)
          </h3>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${muted}`}>Aulas por dia:</span>
            <button
              onClick={() => setPeriods(p => Math.max(pisoDeLinhas, p - 1))}
              disabled={!canEdit || periods <= pisoDeLinhas}
              title={periods <= pisoDeLinhas && maiorPeriodoPreenchido >= periods
                ? `A ${maiorPeriodoPreenchido}ª aula tem disciplina em algum dia. `
                  + 'Limpe essas células para tirar a linha.'
                : 'Menos uma aula por dia'}
              className={`w-7 h-7 rounded-lg text-sm ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'} disabled:opacity-40`}
            >
              −
            </button>
            <span className={`text-sm font-semibold w-6 text-center ${strong}`}>{periods}</span>
            <button
              onClick={() => setPeriods(p => Math.min(MAX_PERIODS, p + 1))}
              disabled={!canEdit || periods >= MAX_PERIODS}
              className={`w-7 h-7 rounded-lg text-sm ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'} disabled:opacity-40`}
            >
              +
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-1">
            <thead>
              <tr>
                <th className={`text-left px-2 py-2 text-xs font-semibold uppercase ${muted}`}>Aula</th>
                {WEEKDAYS.map(d => (
                  <th key={d.value} className={`px-2 py-2 text-xs font-semibold uppercase ${muted}`}>
                    <div>{d.label}</div>
                    {canEdit && d.value > 0 && (
                      <button
                        onClick={() => handleCopyDay(d.value - 1, d.value)}
                        title={`Copiar as aulas de ${WEEKDAYS[d.value - 1].label}`}
                        className={`mt-1 text-[10px] font-normal normal-case px-2 py-0.5 rounded ${darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      >
                        copiar anterior
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: periods }, (_, i) => i + 1).map(period => (
                <tr key={period}>
                  <td className={`px-2 py-2 text-xs font-semibold whitespace-nowrap ${muted}`}>
                    {period}ª aula
                  </td>
                  {WEEKDAYS.map(d => {
                    const key = slotKey(d.value, period);
                    const slot = slots[key];
                    const isEditing = editing && editing.weekday === d.value && editing.period === period;

                    return (
                      <td key={key} className="align-top p-0">
                        {isEditing ? (
                          <SlotEditor
                            darkMode={darkMode}
                            assignments={assignments}
                            subjects={opcoes.subjects}
                            teachers={opcoes.teachers}
                            value={slot}
                            onCancel={() => setEditing(null)}
                            onClear={() => { handleSetSlot(d.value, period, null); setEditing(null); }}
                            onSave={(value) => { handleSetSlot(d.value, period, value); setEditing(null); }}
                          />
                        ) : (
                          <button
                            onClick={() => {
                              if (canEdit) return setEditing({ weekday: d.value, period });
                              if (minhaAula(slot)) return abrirMinhaAula(d.value, period);
                            }}
                            disabled={!canEdit && !minhaAula(slot)}
                            title={
                              minhaAula(slot)
                                ? 'Sua aula — abrir a chamada desta semana'
                                : (ehProfessor && slot ? 'Aula de outro professor' : undefined)
                            }
                            className={`w-full min-h-[64px] rounded-lg px-2 py-2 text-left transition-all ${
                              slot
                                ? `${subjectColor(slot.id_subject)} text-white ${
                                    minhaAula(slot)
                                      ? 'ring-2 ring-white/70 hover:opacity-90 cursor-pointer'
                                      : ehProfessor
                                      ? 'opacity-30 saturate-50 cursor-not-allowed'
                                      : 'hover:opacity-90'
                                  }`
                                : darkMode
                                ? 'bg-slate-700/40 hover:bg-slate-700 text-slate-500 border border-dashed border-slate-600'
                                : 'bg-slate-50 hover:bg-slate-100 text-slate-400 border border-dashed border-slate-300'
                            } ${!canEdit && !minhaAula(slot) ? 'cursor-default' : ''}`}
                          >
                            {slot ? (
                              <>
                                <div className="text-xs font-semibold leading-tight">
                                  {subjectNameById[slot.id_subject] || 'Disciplina removida'}
                                </div>
                                <div className="text-[10px] opacity-90 mt-0.5 truncate">
                                  {teachersById[slot.id_teacher] || '—'}
                                </div>
                                {slot.start_time && (
                                  <div className="text-[10px] opacity-80 mt-0.5">
                                    {slot.start_time}{slot.end_time ? `–${slot.end_time}` : ''}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs">{canEdit ? '+ definir' : '—'}</span>
                            )}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {Object.keys(cargaPorDisciplina).length > 0 && (
          <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${muted}`}>
              Carga horária semanal
            </h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(cargaPorDisciplina).map(([idSubject, count]) => {
                // A carga cadastrada da disciplina é a soma dos vínculos dela
                // na turma — uma mesma disciplina pode ter dois professores.
                const daDisciplina = assignments.filter(
                  a => String(a.id_subject) === String(idSubject) && a.weekly_hours != null
                );
                const declarada = daDisciplina.length
                  ? daDisciplina.reduce((t, a) => t + Number(a.weekly_hours), 0)
                  : null;
                const bate = declarada == null || declarada === count;
                return (
                  <span
                    key={idSubject}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                      darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${subjectColor(idSubject)}`} />
                    {subjectNameById[idSubject] || 'Disciplina'}
                    <strong>{count} aula(s)</strong>
                    {!bate && (
                      <span className="text-[#F9B047]" title={`Carga cadastrada na turma: ${declarada}`}>
                        ≠ {declarada}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Choque de horário: o mesmo professor em duas turmas na mesma aula.
          A grade da escola pode ter arranjos que o sistema não conhece, então
          aqui se avisa e quem decide é a coordenação. */}
      {choques && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-choques"
        >
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white border border-slate-200'} shadow-2xl`}>
            <h2 id="titulo-choques" className={`text-lg font-semibold mb-1 ${strong}`}>
              {choques.length} choque(s) de horário
            </h2>
            <p className={`text-sm mb-4 ${muted}`}>
              Estes professores ficariam em duas turmas ao mesmo tempo — mesmo dia,
              mesma hora. Aula de turno diferente não entra nesta lista. Se for engano,
              volte e ajuste — senão, o painel vai cobrar deles o diário das duas turmas
              no mesmo horário.
            </p>

            <div className={`rounded-lg divide-y max-h-64 overflow-y-auto mb-5 ${darkMode
              ? 'bg-slate-900/60 divide-slate-700' : 'bg-slate-50 divide-slate-200'}`}>
              {choques.map((c, i) => (
                <div key={i} className="px-4 py-2.5">
                  <p className={`text-sm font-medium ${strong}`}>
                    {c.teacher_name} — {c.weekday_display}, {c.period}ª aula
                  </p>
                  <p className={`text-xs ${muted}`}>
                    já está em {c.class_name} ({c.subject_name})
                    {/* Turno e horário do outro lado: é o que permite à
                        coordenação julgar o aviso sem abrir a outra grade. */}
                    {c.shift ? ` · ${c.shift}` : ''}
                    {c.start_time ? ` · ${c.start_time}${c.end_time ? `–${c.end_time}` : ''}` : ''}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setChoques(null)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                Voltar e revisar
              </button>
              <button
                onClick={() => handleSave({ ignorarChoques: true })}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-[#F9B047]/20 text-[#F9B047] hover:bg-[#F9B047]/30'
                  : 'bg-[#F9B047]/15 text-[#B87503] hover:bg-[#F9B047]/25'}`}
              >
                Salvar assim mesmo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Criar o vínculo é o que dá ao professor permissão de escrever no
          diário daquela turma — por isso a coordenação confirma antes. */}
      {confirmarVinculos && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="titulo-vinculos"
        >
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white border border-slate-200'} shadow-2xl`}>
            <h2 id="titulo-vinculos" className={`text-lg font-semibold mb-1 ${strong}`}>
              Confirmar {confirmarVinculos.length} vínculo(s) novo(s)
            </h2>
            <p className={`text-sm mb-4 ${muted}`}>
              Estas combinações ainda não existiam nesta turma. Ao salvar, elas passam
              a valer — e cada professor abaixo ganha permissão de lançar diário e
              frequência da sua disciplina nesta turma.
            </p>

            <div className={`rounded-lg divide-y max-h-64 overflow-y-auto mb-5 ${darkMode
              ? 'bg-slate-900/60 divide-slate-700' : 'bg-slate-50 divide-slate-200'}`}>
              {confirmarVinculos.map(v => (
                <div key={`${v.id_subject}-${v.id_teacher}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${strong}`}>{v.subject_name}</p>
                    <p className={`text-xs truncate ${muted}`}>{v.teacher_name}</p>
                  </div>
                  <span className={`text-xs whitespace-nowrap ${muted}`}>
                    {v.aulas} aula(s)/semana
                  </span>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmarVinculos(null)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                Voltar e revisar
              </button>
              <button
                onClick={() => handleSave({ confirmado: true })}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#13983D] to-[#0974BA] hover:shadow-lg hover:shadow-[#13983D]/25"
              >
                Criar e salvar a grade
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Editor de uma célula da grade: disciplina, professor e horário. */
function SlotEditor({ darkMode, assignments, subjects, teachers, value, onSave, onCancel, onClear }) {
  const [idSubject, setIdSubject] = useState(value?.id_subject || '');
  const [idTeacher, setIdTeacher] = useState(value?.id_teacher || '');
  const [startTime, setStartTime] = useState(value?.start_time || '');
  const [endTime, setEndTime] = useState(value?.end_time || '');

  const inputCls = `w-full px-2 py-1.5 rounded text-xs border focus:outline-none focus:ring-1 focus:ring-[#0974BA] ${
    darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-300 text-slate-700'
  }`;

  // Todas as disciplinas da rede, não só as já atribuídas à turma: é a grade
  // que passa a declarar quem leciona o quê aqui.
  const disciplinas = (subjects && subjects.length)
    ? subjects
    : [...new Map(assignments.map(a => [String(a.id_subject), {
        id: a.id_subject, subject_name: a.subject_name, assigned: true,
      }])).values()];

  // Professores lotados na escola, com os que já lecionam esta disciplina na
  // turma em primeiro lugar — é quase sempre um deles.
  const jaNaDisciplina = new Set(
    assignments
      .filter(a => String(a.id_subject) === String(idSubject))
      .map(a => String(a.id_teacher))
  );
  const professores = [...(teachers || [])].sort((a, b) => {
    const pa = (jaNaDisciplina.has(String(a.id)) ? 0 : 2)
      + ((a.subject_ids || []).map(String).includes(String(idSubject)) ? 0 : 1);
    const pb = (jaNaDisciplina.has(String(b.id)) ? 0 : 2)
      + ((b.subject_ids || []).map(String).includes(String(idSubject)) ? 0 : 1);
    return pa - pb || a.teacher_name.localeCompare(b.teacher_name, 'pt-BR');
  });

  // Um só candidato óbvio para a disciplina: já vem escolhido.
  useEffect(() => {
    if (!idSubject) return;
    const obvios = [...jaNaDisciplina];
    if (obvios.length === 1) setIdTeacher(obvios[0]);
  }, [idSubject]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`min-h-[64px] rounded-lg p-2 space-y-1.5 ${darkMode ? 'bg-slate-900 border border-[#0974BA]' : 'bg-white border border-[#0974BA]'}`}>
      {/* A célula da grade é apertada, e a lista de professores da rede é
          longa: é aqui que a busca mais poupa rolagem. */}
      <SearchableSelect
        value={idSubject}
        onChange={setIdSubject}
        options={disciplinas.map(s => ({ value: s.id, label: s.subject_name }))}
        emptyOptionLabel="Disciplina..."
        searchPlaceholder="Buscar disciplina..."
        darkMode={darkMode}
        autoFocus
      />

      {idSubject && (
        <SearchableSelect
          value={idTeacher}
          onChange={setIdTeacher}
          options={professores.map(t => ({
            value: t.id,
            label: `${t.teacher_name}${jaNaDisciplina.has(String(t.id)) ? '' : ' · novo vínculo'}`,
          }))}
          emptyOptionLabel="Professor..."
          searchPlaceholder="Buscar professor..."
          darkMode={darkMode}
        />
      )}

      <div className="flex gap-1">
        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
        <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputCls} />
      </div>

      <div className="flex gap-1">
        <button
          onClick={() => onSave({
            id_subject: idSubject,
            id_teacher: idTeacher,
            start_time: startTime,
            end_time: endTime,
          })}
          disabled={!idSubject || !idTeacher}
          className="flex-1 px-2 py-1 rounded text-[11px] font-medium text-white bg-[#13983D] disabled:opacity-40"
        >
          OK
        </button>
        <button
          onClick={onClear}
          className="px-2 py-1 rounded text-[11px] font-medium bg-[#E83A3C]/15 text-[#E83A3C]"
          title="Limpar esta aula"
        >
          ✕
        </button>
        <button
          onClick={onCancel}
          className={`px-2 py-1 rounded text-[11px] ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default GradeHoraria;
